/**
 * @fileoverview Seven-stage department authentication setup and management wizard.
 * @module system-web/components/auth-setup/DepartmentAuthSetupWizard
 *
 * Responsibilities:
 * - Present and edit department authentication configuration across setup stages.
 * - Require confirmation before changes invalidate an existing IT handoff.
 *
 * Dependencies:
 * - useDepartmentAuthSetup for local-first state and setup actions.
 * - ConfirmActionModal and setup packet presentation components.
 *
 * Architecture:
 * - Composes the local-first setup hook with stage-specific form presentation.
 *
 * Notes:
 * - Client secrets are write-only and never restored into form state.
 *
 * @component
 * @localFirst Reads and mutates setup state only through useDepartmentAuthSetup.
 * @security Client secrets are write-only; UUID secret IDs are rejected.
 */
import { useEffect, useState } from 'react';
import { useDepartmentAuthSetup } from '../../hooks/useDepartmentAuthSetup';
import ConfirmActionModal from '../ui/ConfirmActionModal';
import { DefensibleLogo } from '@ui-common';
import ItSetupPacketPreviewModal from './ItSetupPacketPreviewModal';

const stages = ['Choose method', 'JIT access', 'IT packet', 'IT configuration', 'Validate', 'Activate', 'Manage'];
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const onMicrosoftDomain = /^[a-z0-9-]+\.onmicrosoft\.com$/i;
const emailDomain = /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i;
const confirmationLabels = [
  'Both redirect URIs are registered under the Web platform (not SPA).',
  'Admin consent was granted for openid, profile, email, and User.Read.',
  'Optional ID token claims are configured: email, given_name, family_name, upn.',
  'Enterprise App assignment is required and approved users/groups are assigned.',
];
const confirmationKeys = [
  'redirectUrisRegistered',
  'graphPermissionsConsented',
  'optionalClaimsConfigured',
  'userAssignmentRequiredEnabled',
];

export interface DepartmentAuthSetupWizardProps {
  tenantId: string;
  departmentName?: string;
  onClose: () => void;
}

interface AuthSetupDraftValues {
  providerName: string;
  upstreamTenantId: string;
  domain: string;
  itName: string;
  itEmail: string;
  role: string;
  emailDomains: string;
}

function buildAuthSetupDraft(values: AuthSetupDraftValues) {
  return {
    authMethod: 'microsoft365' as const,
    providerType: 'microsoft_entra_oidc',
    name: values.providerName.trim(),
    upstreamTenantId: values.upstreamTenantId.trim() || undefined,
    onMicrosoftDomain: values.domain.trim() || undefined,
    departmentItContact: {
      name: values.itName.trim(),
      email: values.itEmail.trim(),
    },
    jitProvisioning: {
      enabled: true,
      defaultRoleName: values.role,
      allowedEmailDomains: values.emailDomains.split(',').map((value) => value.trim()).filter(Boolean),
    },
  };
}

export default function DepartmentAuthSetupWizard({ tenantId, departmentName, onClose }: DepartmentAuthSetupWizardProps) {
  const setup = useDepartmentAuthSetup(tenantId);
  const settings = setup.state?.tenant?.authenticationSettings ?? {};
  const providerSettings = settings.federatedProvider ?? {};
  const [providerName, setProviderName] = useState(providerSettings.name ?? `${departmentName ?? 'Department'} Microsoft 365`);
  const [itName, setItName] = useState(settings.departmentItContact?.name ?? '');
  const [itEmail, setItEmail] = useState(settings.departmentItContact?.email ?? '');
  const [role, setRole] = useState(settings.jitProvisioning?.defaultRoleName ?? 'Inspector');
  const [upstreamTenantId, setUpstreamTenantId] = useState(providerSettings.upstreamTenantId ?? '');
  const [domain, setDomain] = useState(providerSettings.onMicrosoftDomain ?? '');
  const [clientId, setClientId] = useState(providerSettings.clientId ?? '');
  const [secret, setSecret] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [emailDomains, setEmailDomains] = useState<string>(settings.jitProvisioning?.allowedEmailDomains?.join(', ') ?? '');
  const [confirmations, setConfirmations] = useState<boolean[]>(confirmationLabels.map(() => false));
  const [secretError, setSecretError] = useState('');
  const [formError, setFormError] = useState('');
  const [packet, setPacket] = useState<any>(null);
  const [handoff, setHandoff] = useState<any>(null);
  const [diagnostics, setDiagnostics] = useState<any>(null);
  const [testUrl, setTestUrl] = useState('');
  const [revertReason, setRevertReason] = useState('');
  const [revertSlug, setRevertSlug] = useState('');
  const [sendConfirmOpen, setSendConfirmOpen] = useState(false);
  const [activateConfirmOpen, setActivateConfirmOpen] = useState(false);
  const [revertConfirmOpen, setRevertConfirmOpen] = useState(false);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [loadedTenantId, setLoadedTenantId] = useState('');
  const [loadedDraftSnapshot, setLoadedDraftSnapshot] = useState<string | null>(null);
  const [selectedPacketVersion, setSelectedPacketVersion] = useState<number | ''>('');
  const [activeStep, setActiveStep] = useState(0);
  const stageIndexByName: Record<string, number> = {
    choose: 0, jit: 1, packet: 2, credentials: 3, validate: 4, activate: 5, manage: 6,
  };
  const stageIndex = stageIndexByName[setup.stage] ?? 0;
  const futureExpiry = expiresAt !== '' && new Date(`${expiresAt}T23:59:59`).getTime() >= Date.now() + 7 * 86400000;
  const emailDomainsValid = emailDomains.split(',').map((value) => value.trim()).filter(Boolean).every((value) => emailDomain.test(value));
  const isSecretId = uuid.test(secret.trim());
  const slug = setup.state?.tenant?.slug ?? '';
  const currentMethod = setup.state?.authMethod ?? 'local';
  const stageStatus = setup.state?.setupStatus ?? 'not_configured';
  const validation = settings.validation ?? {};
  const setupPackets = setup.state?.setupPackets ?? [];

  useEffect(() => {
    if (!setup.state?.tenant || loadedTenantId === tenantId) return;
    const auth = setup.state.tenant.authenticationSettings ?? {};
    const provider = auth.federatedProvider ?? {};
    const draftValues: AuthSetupDraftValues = {
      providerName: provider.name ?? `${departmentName ?? 'Department'} Microsoft 365`,
      upstreamTenantId: provider.upstreamTenantId ?? '',
      domain: provider.onMicrosoftDomain ?? '',
      itName: auth.departmentItContact?.name ?? '',
      itEmail: auth.departmentItContact?.email ?? '',
      role: auth.jitProvisioning?.defaultRoleName ?? 'Inspector',
      emailDomains: auth.jitProvisioning?.allowedEmailDomains?.join(', ') ?? '',
    };
    setProviderName(draftValues.providerName);
    setItName(draftValues.itName);
    setItEmail(draftValues.itEmail);
    setRole(draftValues.role);
    setUpstreamTenantId(draftValues.upstreamTenantId);
    setDomain(draftValues.domain);
    setClientId(provider.clientId ?? '');
    setEmailDomains(draftValues.emailDomains);
    setLoadedDraftSnapshot(JSON.stringify(buildAuthSetupDraft(draftValues)));
    setSelectedPacketVersion(auth.setupPackets?.at(-1)?.version ?? '');
    setActiveStep(stageIndexByName[setup.stage] ?? 0);
    setLoadedTenantId(tenantId);
  }, [departmentName, loadedTenantId, setup.stage, setup.state?.tenant, tenantId]);

  const saveDraft = async (): Promise<boolean> => {
    setFormError('');
    if (providerName.trim().length < 2 || itName.trim().length < 2 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(itEmail.trim())) {
      setFormError('Enter a provider display name, Department IT contact name, and valid contact email.');
      return false;
    }
    const draft = buildAuthSetupDraft({
      providerName,
      upstreamTenantId,
      domain,
      itName,
      itEmail,
      role,
      emailDomains,
    });
    await setup.saveDraft(draft);
    setLoadedDraftSnapshot(JSON.stringify(draft));
    return true;
  };

  const continueWizard = async () => {
    if (activeStep <= 1) {
      if (loadedDraftSnapshot === null) return;
      const currentDraft = buildAuthSetupDraft({
        providerName,
        upstreamTenantId,
        domain,
        itName,
        itEmail,
        role,
        emailDomains,
      });
      const draftIsDirty = JSON.stringify(currentDraft) !== loadedDraftSnapshot;
      const isInitialSetup = !stageStatus || stageStatus === 'not_configured';
      if (isInitialSetup || draftIsDirty) {
        if (draftIsDirty && ['awaiting_it', 'it_submitted', 'ready_to_validate', 'validation_failed', 'validated'].includes(stageStatus)) {
          setResetConfirmOpen(true);
          return;
        }
        if (!await saveDraft()) return;
      }
    }
    setActiveStep((step) => Math.min(stages.length - 1, step + 1));
  };

  const submitCredentials = async () => {
    setSecretError('');
    setFormError('');
    if (isSecretId) {
      setSecretError('You pasted the Client Secret ID (a GUID), not the Client Secret Value. In Microsoft Entra → Certificates & secrets, copy the Value column.');
      return;
    }
    if (!uuid.test(upstreamTenantId.trim()) || !uuid.test(clientId.trim())) {
      setFormError('Enter valid Directory (tenant) ID and Application (client) ID GUIDs.');
      return;
    }
    if (!onMicrosoftDomain.test(domain.trim())) {
      setFormError('Enter the primary tenant domain ending in .onmicrosoft.com.');
      return;
    }
    if (!emailDomainsValid || !emailDomains.trim()) {
      setFormError('Enter one or more valid verified organizational email domains separated by commas.');
      return;
    }
    if (!futureExpiry) {
      setFormError('The client secret expiration date must be at least seven days in the future.');
      return;
    }
    if (!secret.trim()) {
      setFormError('Enter the Client Secret Value.');
      return;
    }
    if (!confirmations.every(Boolean)) {
      setFormError('Confirm all four Microsoft Entra configuration requirements before submitting.');
      return;
    }
    try {
      await setup.submitCredentials({
        upstreamTenantId: upstreamTenantId.trim(),
        onMicrosoftDomain: domain.trim(),
        clientId: clientId.trim(),
        clientSecret: secret,
        secretExpiresAt: new Date(`${expiresAt}T23:59:59`).toISOString(),
        verifiedEmailDomains: emailDomains.split(',').map((value) => value.trim()).filter(Boolean),
        itConfirmations: Object.fromEntries(confirmationKeys.map((key, index) => [key, confirmations[index]])),
      });
      setSecret('');
      setSecretError('');
      setFormError('');
    } catch {
      setSecret('');
    }
  };

  const runValidation = async () => {
    try {
      setDiagnostics(await setup.validateServer());
    } catch (error) {
      setDiagnostics({ error: error instanceof Error ? error.message : 'Server validation failed.' });
    }
  };

  return (
    <div className="fixed inset-0 z-40 overflow-auto bg-black/30 p-4">
      <section className="mx-auto max-w-5xl rounded-xl bg-white p-6 shadow-xl">
        <header className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <DefensibleLogo size="md" subtitle="System Web" />
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500">Department authentication</p>
              <h2 className="text-2xl font-semibold">{departmentName ?? 'Department'}</h2>
              <p className="mt-1 text-sm text-slate-600">Currently active: {currentMethod === 'local' ? 'Local accounts' : 'Microsoft 365 / Entra'} · Setup: {stageStatus}</p>
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close authentication setup">Close</button>
        </header>

        <p className="mt-5 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
          Your current sign-in stays active while you configure Microsoft Entra. The department switches only after validation and a verified Department Admin sign-in.
        </p>
        <div className="mt-4 flex flex-wrap gap-2" aria-label="Authentication setup status">
          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">Current: {currentMethod === 'local' ? 'Local accounts' : 'Microsoft 365 / Entra'}</span>
          <span className={`rounded-full px-3 py-1 text-xs font-semibold ${stageStatus === 'active' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>Setup: {stageStatus.replace(/_/g, ' ')}</span>
          {providerSettings.targetAuthMethod && currentMethod === 'local' && <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-900">Staged: Microsoft 365 / Entra</span>}
        </div>
        <ol className="mt-5 grid grid-cols-2 gap-1 sm:grid-cols-4 lg:grid-cols-7" aria-label="Authentication setup stages">
          {stages.map((name, index) => (
            <li key={name}>
              <button type="button" aria-current={index === activeStep ? 'step' : undefined} onClick={() => setActiveStep(index)}
                className={`w-full rounded-lg border px-2 py-3 text-left text-xs ${index < stageIndex ? 'border-green-200 bg-green-50 text-green-900' : index === activeStep ? 'border-orange-400 bg-orange-50 text-slate-900' : 'border-slate-200 bg-white text-slate-500'}`}>
                <span className="mb-1 block font-semibold">{index < stageIndex ? '✓' : `0${index + 1}`}</span>{name}
              </button>
            </li>
          ))}
        </ol>
        {!setup.isOnline && <p className="mt-4 rounded bg-amber-50 p-3 text-sm text-amber-800">You are offline. Authentication setup actions are disabled until you reconnect.</p>}
        {setup.error && <p role="alert" className="mt-4 rounded bg-red-50 p-3 text-sm text-red-800">{setup.error.message}</p>}
        {formError && <p role="alert" className="mt-4 rounded bg-red-50 p-3 text-sm text-red-800">{formError}</p>}

        <div className="mt-6 min-h-[360px] rounded-xl border border-slate-200 bg-white p-5">
          {activeStep === 0 && <section aria-labelledby="auth-stage-provider">
            <h3 id="auth-stage-provider" className="text-lg font-semibold">1. Choose a sign-in method</h3>
            <p className="mt-1 text-sm text-slate-600">Microsoft 365 / Entra is available now. Other providers are planned for a future release.</p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <label className="rounded border border-blue-400 p-3">Microsoft 365 / Microsoft Entra ID (OIDC) — Available now <input type="radio" checked readOnly /></label>
              {['Google Workspace', 'SAML', 'Generic OIDC'].map((name) => <label key={name} className="rounded border p-3 text-slate-400">{name} — Future release <input type="radio" disabled /></label>)}
            </div>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <label>Login button display name<input aria-label="Provider display name" className="mt-1 w-full rounded border p-2" value={providerName} onChange={(event) => setProviderName(event.target.value)} /></label>
              <label>Department IT contact name<input aria-label="Department IT contact name" className="mt-1 w-full rounded border p-2" value={itName} onChange={(event) => setItName(event.target.value)} /></label>
              <label>Department IT contact email<input type="email" aria-label="Department IT contact email" className="mt-1 w-full rounded border p-2" value={itEmail} onChange={(event) => setItEmail(event.target.value)} /></label>
              <label>Primary tenant domain<input aria-label="Microsoft tenant domain" placeholder="Primary tenant domain ending in .onmicrosoft.com" className="mt-1 w-full rounded border p-2" value={domain} onChange={(event) => setDomain(event.target.value)} /></label>
              <label>Microsoft Entra Directory (tenant) ID<input aria-label="Directory tenant ID" className="mt-1 w-full rounded border p-2" value={upstreamTenantId} onChange={(event) => setUpstreamTenantId(event.target.value)} /></label>
            </div>
            <button type="button" disabled={!setup.isOnline || setup.pending} onClick={() => void saveDraft()} className="mt-3 rounded bg-blue-700 px-3 py-2 text-white disabled:opacity-50">Save draft</button>
          </section>}

          {activeStep === 1 && <section aria-labelledby="auth-stage-jit">
            <h3 id="auth-stage-jit" className="text-lg font-semibold">2. Set access and first-sign-in policy</h3>
            <p className="mt-1 text-sm text-slate-600">Choose the default role for eligible new users and the email domains they must use. Department Admin is never assigned automatically.</p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <label>Default non-admin JIT role<select aria-label="Default non-admin JIT role" className="mt-1 block rounded border p-2" value={role} onChange={(event) => setRole(event.target.value)}><option value="Inspector">Inspector</option><option value="Viewer">Viewer</option></select></label>
              <label>Verified email domains<input aria-label="Verified email domains" placeholder="Enter each verified domain, separated by commas" className="mt-1 block w-full rounded border p-2" value={emailDomains} onChange={(event) => setEmailDomains(event.target.value)} /></label>
            </div>
          </section>}

          {activeStep === 2 && <section aria-labelledby="auth-stage-packet">
            <h3 id="auth-stage-packet" className="text-lg font-semibold">3. Prepare the IT setup packet</h3>
            <p className="mt-1 text-sm text-slate-600">Generating the versioned HTML packet never sends an email.</p>
            <button type="button" disabled={!setup.isOnline || setup.pending} onClick={() => void setup.generatePacket().then((response) => {
              const generatedPacket = response?.packet ?? response;
              setPacket(generatedPacket ? { ...generatedPacket, html: undefined } : null);
              setSelectedPacketVersion(response?.packet?.version ?? '');
            })} className="mt-3 rounded border px-3 py-2 disabled:opacity-50">Generate / regenerate packet</button>
            {setupPackets.length > 0 && <label className="ml-2 text-sm">Version<select aria-label="Packet version" className="ml-1 rounded border p-2" value={selectedPacketVersion} onChange={(event) => setSelectedPacketVersion(Number(event.target.value))}>{setupPackets.map((item) => <option key={item.version} value={item.version}>{item.version}{item.status === 'superseded' ? ' — superseded' : ''}</option>)}</select></label>}
            <button type="button" disabled={!setup.isOnline || setup.pending || !setupPackets.length} onClick={() => {
              const version = selectedPacketVersion || setupPackets.at(-1)?.version || packet?.version || 1;
              void setup.getPacket(version).then((response) => setPacket(response?.packet ? { ...response.packet, html: response.html } : response));
            }} className="ml-2 rounded border px-3 py-2 disabled:opacity-50">Preview packet</button>
            {packet && <p className="mt-2 text-sm">Packet version {packet.version} · {packet.referenceId ?? ''}</p>}
          </section>}

          {activeStep === 3 && <section aria-labelledby="auth-stage-credentials">
            <h3 id="auth-stage-credentials" className="text-lg font-semibold">4. Collect Microsoft Entra configuration</h3>
            <p className="mt-1 text-sm text-slate-600">Use the values from your registered app. The secret value is sent securely and is never displayed again.</p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <label>Application (client) ID<input aria-label="Client ID" className="mt-1 w-full rounded border p-2" value={clientId} onChange={(event) => setClientId(event.target.value)} /></label>
              <label>Client secret Value<input aria-label="Client secret value" type="password" autoComplete="new-password" className="mt-1 w-full rounded border p-2" value={secret} onChange={(event) => setSecret(event.target.value)} /></label>
              <label>Secret expiration date<input aria-label="Secret expiration date" type="date" className="mt-1 block rounded border p-2" value={expiresAt} onChange={(event) => setExpiresAt(event.target.value)} /></label>
            </div>
            {secretError && <p role="alert" className="mt-2 text-sm text-red-700">{secretError}</p>}
            <fieldset className="mt-4 space-y-2">
              <legend className="text-sm font-medium">Department IT completion confirmations</legend>
              {confirmationLabels.map((label, index) => <label key={confirmationKeys[index]} className="flex gap-2 text-sm"><input type="checkbox" checked={confirmations[index]} onChange={(event) => setConfirmations((current) => current.map((value, item) => item === index ? event.target.checked : value))} />{label}</label>)}
            </fieldset>
            <button type="button" disabled={!setup.isOnline || setup.pending} onClick={() => void submitCredentials()} className="mt-3 rounded bg-blue-700 px-3 py-2 text-white disabled:opacity-50">Submit credentials</button>
            <button type="button" disabled={!setup.isOnline || setup.pending || !['awaiting_it', 'active'].includes(stageStatus)} onClick={() => void setup.createHandoffLink(false).then(setHandoff)} className="ml-2 rounded border px-3 py-2 disabled:opacity-50">{stageStatus === 'active' ? 'Create secret rotation link' : 'Create secure handoff link'}</button>
            <button type="button" disabled={!setup.isOnline || setup.pending || !['awaiting_it', 'active'].includes(stageStatus)} onClick={() => setSendConfirmOpen(true)} className="ml-2 rounded border px-3 py-2 disabled:opacity-50">{stageStatus === 'active' ? 'Send rotation link by email' : 'Send packet + handoff link by email'}</button>
            {handoff?.url && <p className="mt-3 break-all text-sm">Secure handoff link (expires {new Date(handoff.expiresAt).toLocaleString()}): <a className="text-blue-700 underline" href={handoff.url}>{handoff.url}</a></p>}
          </section>}

          {activeStep === 4 && <section aria-labelledby="auth-stage-validation">
            <h3 id="auth-stage-validation" className="text-lg font-semibold">5. Validate and test sign-in</h3>
            <button type="button" disabled={!setup.isOnline || setup.pending || !['ready_to_validate', 'validation_failed'].includes(stageStatus)} onClick={() => void runValidation()} className="rounded border px-3 py-2 disabled:opacity-50">Run server validation &amp; sync External ID</button>
            <button type="button" disabled={!setup.isOnline || setup.pending} onClick={() => void setup.createTestSession().then((value) => setTestUrl(value.url ?? ''))} className="ml-2 rounded border px-3 py-2 disabled:opacity-50">Start interactive test sign-in</button>
            <ul className="mt-3 list-inside list-disc text-sm text-slate-700">
              <li>Server validation: {validation.serverCheckStatus ?? 'not run'}</li>
              <li>Interactive sign-in: {validation.interactiveTestStatus ?? 'not run'}</li>
              <li>Interactive test identity: {validation.interactiveTestUserEmail ?? 'not confirmed'}</li>
            </ul>
            {diagnostics && <div aria-label="Server validation diagnostics" className={`mt-3 rounded-lg border p-3 text-sm ${diagnostics.error ? 'border-red-200 bg-red-50 text-red-900' : 'border-green-200 bg-green-50 text-green-900'}`}>
              {diagnostics.error
                ? <><strong>Server validation failed</strong><p className="mt-1">{diagnostics.error}</p></>
                : <><strong>Configuration check complete</strong><p className="mt-1">The server checked the tenant domain, application credentials, and Microsoft Graph connectivity.</p></>}
            </div>}
            {testUrl && <p className="mt-3 break-all text-sm">Interactive test URL: <a href={testUrl} className="text-blue-700 underline">{testUrl}</a></p>}
            <p className="mt-2 text-sm text-slate-600">The server will block activation until validation, interactive sign-in, and an active Department Admin federated sign-in are all verified.</p>
          </section>}

          {activeStep === 5 && <section aria-labelledby="auth-stage-activation">
            <h3 id="auth-stage-activation" className="text-lg font-semibold">6. Confirm readiness and activate</h3>
            <p className="mt-1 text-sm text-slate-600">Activation is blocked until server validation, interactive sign-in, and an active Department Admin sign-in are verified.</p>
            <button type="button" disabled={!setup.isOnline || setup.pending || setup.stage !== 'activate'} onClick={() => setActivateConfirmOpen(true)} className="rounded bg-green-700 px-3 py-2 text-white disabled:opacity-50">Activate &amp; lock in Microsoft 365 OIDC</button>
          </section>}

          {activeStep === 6 && <section aria-labelledby="auth-stage-manage">
            <h3 id="auth-stage-manage" className="text-lg font-semibold">7. Manage department sign-in</h3>
            {providerSettings.credentialMetadata?.expiresAt && <p className="mt-2 text-sm">Client secret expires {new Date(providerSettings.credentialMetadata.expiresAt).toLocaleDateString()}.</p>}
            <label className="mt-3 block">Reason for reverting to local authentication<input aria-label="Revert reason" className="mt-1 block w-full rounded border p-2" value={revertReason} onChange={(event) => setRevertReason(event.target.value)} /></label>
            <label className="mt-3 block">Type department slug to confirm ({slug})<input aria-label="Confirm department slug" className="mt-1 block w-full rounded border p-2" value={revertSlug} onChange={(event) => setRevertSlug(event.target.value)} /></label>
            <button type="button" disabled={!setup.isOnline || setup.pending || !revertReason.trim() || !slug || revertSlug !== slug} onClick={() => setRevertConfirmOpen(true)} className="mt-3 rounded border border-red-300 px-3 py-2 text-red-700 disabled:opacity-50">Revert department to local authentication</button>
          </section>}
          <footer className="mt-7 flex justify-between border-t border-slate-200 pt-4">
            <button type="button" disabled={activeStep === 0} onClick={() => setActiveStep((step) => Math.max(0, step - 1))}
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm disabled:opacity-40">Back</button>
            {activeStep < stages.length - 1 && <button type="button" disabled={!setup.isOnline || setup.pending}
              onClick={() => void continueWizard()}
              className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
              {setup.pending ? 'Saving…' : activeStep === 1 ? 'Save policy and continue' : 'Continue'}
            </button>}
          </footer>
        </div>
      </section>

      {packet && <ItSetupPacketPreviewModal packet={packet} currentVersion={setupPackets.at(-1)?.version}
        departmentName={departmentName ?? setup.state?.tenant?.name} departmentSlug={slug}
        onClose={() => setPacket(null)} />}
      {sendConfirmOpen && <ConfirmActionModal title="Send IT setup packet and handoff link" description={`Send the secure one-time handoff link to ${itEmail || setup.state?.tenant?.authenticationSettings?.departmentItContact?.email || 'the Department IT contact'}?`} warning="The link expires in seven days and may be used once. The setup packet and email will not contain a client secret." confirmButtonLabel="Send packet and link" isSubmitting={setup.pending} onClose={() => setSendConfirmOpen(false)} onConfirm={async () => {
        const result = await setup.createHandoffLink(true);
        setHandoff(result);
        setSendConfirmOpen(false);
      }} />}
      {activateConfirmOpen && <ConfirmActionModal title="Activate Microsoft 365 OIDC" description={`Activating Microsoft 365 OIDC will make it the only authentication method for ${departmentName ?? 'this department'}. Local email/password sign-in will be disabled.`} warning="Confirm the Department Admin has completed a verified federated sign-in before proceeding." confirmButtonLabel="Activate and lock in" isSubmitting={setup.pending} onClose={() => setActivateConfirmOpen(false)} onConfirm={async () => {
        await setup.activate();
        setActivateConfirmOpen(false);
      }} />}
      {revertConfirmOpen && <ConfirmActionModal title="Revert department to local authentication" description={`Revert ${departmentName ?? 'this department'} to local authentication? Existing federated identities are retained.`} warning={revertReason} confirmButtonLabel="Revert to local" isSubmitting={setup.pending} onClose={() => setRevertConfirmOpen(false)} onConfirm={async () => {
        await setup.revertToLocal(revertReason.trim());
        setRevertConfirmOpen(false);
      }} />}
      {resetConfirmOpen && <ConfirmActionModal title="Save authentication setup changes?" description="Continue with the updated authentication settings?" warning="Saving these changes returns setup to draft and invalidates any IT handoff link already sent." confirmButtonLabel="Save changes and continue" isSubmitting={setup.pending} onClose={() => setResetConfirmOpen(false)} onConfirm={async () => {
        if (await saveDraft()) {
          setResetConfirmOpen(false);
          setActiveStep((step) => Math.min(stages.length - 1, step + 1));
        }
      }} />}
    </div>
  );
}
